import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { SearchBar } from '../../components/videoIndex'; // adjust path accordingly
import MainLoader from '../../components/Structures/MainLoader';

const LIVE_URL = process.env.REACT_APP_LIVES_SERVER_URL || 'https://s1vzh3gy9i5nqs-8080.proxy.runpod.net';
const WEBSOCKET_URL = process.env.REACT_APP_LIVES_WS_URI || 'wss://s1vzh3gy9i5nqs-8080.proxy.runpod.net';

// Helper function to group consecutive transcript chunks by speaker
function groupConsecutiveBySpeaker(chunks) {
  const grouped = [];
  let currentSpeaker = null;
  let currentText = '';

  for (const chunk of chunks) {
    const speaker = chunk.speaker || 'Unknown';
    if (speaker === currentSpeaker) {
      currentText += ' ' + chunk.text;
    } else {
      if (currentSpeaker !== null) {
        grouped.push({ speaker: currentSpeaker, text: currentText.trim() });
      }
      currentSpeaker = speaker;
      currentText = chunk.text;
    }
  }
  if (currentSpeaker !== null) {
    grouped.push({ speaker: currentSpeaker, text: currentText.trim() });
  }
  return grouped;
}

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightTextReact(text, searchTerm) {
  if (!searchTerm) return [text];
  const escapedTerm = escapeRegExp(searchTerm);
  const regex = new RegExp(escapedTerm, 'gi');
  let lastIndex = 0;
  const result = [];
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Append the text before the match as a plain text node
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    // Wrap the matched text in a span with custom inline style
    result.push(
      <span
        key={key++}
        style={{
          backgroundColor: 'yellow',
          display: 'inline', // ensure it's inline
          whiteSpace: 'normal', // allow wrapping within the span
        }}
      >
        {match[0]}
      </span>
    );
    lastIndex = regex.lastIndex;
  }

  // Append any remaining text after the last match
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  return result;
}

function Streaming() {
  const { liveId } = useParams();
  const [searchParams] = useSearchParams();

  // Refs for video and transcript container
  const videoRef = useRef(null);
  const transcriptRef = useRef(null);

  // States for transcripts, auto-scroll, WebSocket connection, search, and archive loading
  const [transcripts, setTranscripts] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [search, setSearch] = useState('');
  const [matchIndices, setMatchIndices] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [loadingArchive, setLoadingArchive] = useState(false);

  // Query parameters
  const title = searchParams.get('title');
  const isOnLive = searchParams.get('isOnLive') === 'true';

  // Derived URLs for HLS stream and WebSocket
  const hlsStreamUrl = `${LIVE_URL}/hls/${liveId}/index.m3u8`;
  const wsUri = `${WEBSOCKET_URL}/ws?videoId=${liveId}`;

  // Fetch archived transcript if not live
  useEffect(() => {
    if (!isOnLive) {
      setLoadingArchive(true);
      const archiveEndpoint = `${LIVE_URL}/app/live/${liveId}`;
      const plainAxios = axios.create({});
      plainAxios
        .get(archiveEndpoint)
        .then((res) => {
          setTranscripts(res.data);
          setLoadingArchive(false);
        })
        .catch((err) => {
          console.error('Error fetching archived transcript:', err);
          setLoadingArchive(false);
        });
    }
  }, [isOnLive, liveId]);

  // For live mode: set up HLS playback and WebSocket connection
  useEffect(() => {
    if (!isOnLive) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(hlsStreamUrl);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoRef.current.play().catch((err) => console.warn('Auto play blocked:', err));
      });
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = hlsStreamUrl;
      videoRef.current.play().catch((err) => console.warn('Auto play blocked:', err));
    }

    const ws = new WebSocket(wsUri);
    ws.onopen = () => {
      console.log('WebSocket connected for transcripts');
      setWsConnected(true);
    };
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === 'fullTranscript') {
        setTranscripts(data.data);
      } else {
        setTranscripts((prev) => [...prev, data]);
      }
    };
    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
    };
    return () => ws.close();
  }, [isOnLive, hlsStreamUrl, wsUri]);

  // Auto-scroll effect: scroll transcript container to bottom when no search results
  useEffect(() => {
    if (autoScroll && transcriptRef.current && matchIndices.length === 0) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcripts, autoScroll, matchIndices]);

  // Group transcripts by speaker for display
  const groupedTranscripts = groupConsecutiveBySpeaker(transcripts);

  const handleSearch = (query) => {
    setSearch(query);
    if (!query) {
      setMatchIndices([]);
      setCurrentMatchIndex(0);
      setAutoScroll(true); // re-enable auto-scroll if search is cleared
      return;
    }
    const matches = groupedTranscripts.reduce((acc, item, idx) => {
      if (item.text.toLowerCase().includes(query.toLowerCase())) {
        acc.push(idx);
      }
      return acc;
    }, []);
    setMatchIndices(matches);
    if (matches.length > 0) {
      setCurrentMatchIndex(0);
      setAutoScroll(false); // disable auto-scroll when search returns results
    } else {
      setCurrentMatchIndex(-1);
    }
  };

  // Handle navigation among search results using the SearchBar controls
  const onChangeLocation = (direction) => {
    if (matchIndices.length === 0) return;
    if (direction === 'down') {
      setCurrentMatchIndex((prev) => (prev + 1) % matchIndices.length);
    } else if (direction === 'up') {
      setCurrentMatchIndex((prev) => (prev - 1 + matchIndices.length) % matchIndices.length);
    }
  };

  // Scroll to a specific paragraph element by its index
  const scrollToParagraph = (paragraphIndex) => {
    const paragraphElement = document.getElementById(`p-${paragraphIndex}`);
    const container = transcriptRef.current;
    if (paragraphElement && container) {
      container.scrollTo({
        top: paragraphElement.offsetTop - container.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  // When the current match index changes, scroll to that paragraph
  useEffect(() => {
    if (matchIndices.length > 0 && currentMatchIndex >= 0) {
      const targetParagraph = matchIndices[currentMatchIndex];
      scrollToParagraph(targetParagraph);
    }
  }, [currentMatchIndex, matchIndices]);

  const renderTranscript = () => {
    return groupedTranscripts.map((item, i) => {
      const highlightedNodes = search ? highlightTextReact(item.text, search) : [item.text];
      return (
        <>
          <p key={`p-${i}`} id={`p-${i}`} className="w-full">
            <strong className="font-bold">Speaker {item.speaker}:</strong> {highlightedNodes}
          </p>
          <br />
        </>
      );
    });
  };

  return (
    <div className="w-full flex gap-5">
      {/* Video Section */}
      <div className="w-[60%] h-full overflow-hidden">
        <div className="py-[15px] px-5 border border-[#E5E7EB] bg-white mb-4">
          <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
            <span className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8">
              Recording: <strong>{title}</strong>
            </span>
          </div>
          <div className="w-full flex justify-center">
            {!isOnLive ? (
              <div className="p-4 text-gray-600">This video is archived. No live stream available.</div>
            ) : (
              <video ref={videoRef} controls autoPlay style={{ width: '100%' }} />
            )}
          </div>
        </div>
      </div>

      {/* Transcript Section */}
      <div className="flex flex-col h-full max-h-screen w-[60%]">
        <div className="flex gap-3">
          <SearchBar
            handleSearch={handleSearch}
            counter={matchIndices.length}
            location={matchIndices.length > 0 ? currentMatchIndex + 1 : 0}
            onChangeLocation={onChangeLocation}
            placeholder="Search transcript..."
            search={search}
            setSearch={setSearch}
            autoSearch
          />
          <button
            className="py-[9px] px-8 bg-[#ECEFF4] text-base font-normal text-black transition-all duration-500 hover:bg-lightblue-hover hover:text-white"
            onClick={() => handleSearch(search)}
          >
            Search
          </button>
        </div>
        <div className="h-full max-h-[calc(100vh-220px)] flex-1 flex flex-col py-[15px] px-2.5 sm:px-3 border border-[#E5E7EB] bg-white mt-2">
          <div className="border-b border-[#E5E7EB] mb-5 pb-2.5 flex items-center justify-between">
            <h2 className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8">
              {isOnLive ? 'Live Transcript' : 'Archived Transcript'}
            </h2>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
              Auto-scroll
            </label>
          </div>
          <div className="w-full overflow-y-auto overflow-x-hidden flex flex- flex-col" ref={transcriptRef}>
            {renderTranscript()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Streaming;
